import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./Cart.css";
import { useCart } from "../../context/CartContext";

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, getCartTotal } = useCart();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      x: -100,
      transition: { duration: 0.3 },
    },
  };

  return (
    <div className="cart-page">
      <motion.div
        className="cart-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header Section */}
        <div className="cart-header">
          <motion.h1 variants={itemVariants}>Shopping Cart</motion.h1>
          {cartItems.length > 0 && (
            <motion.p className="cart-item-count" variants={itemVariants}>
              {cartItems.length} {cartItems.length === 1 ? "item" : "items"} in
              your cart
            </motion.p>
          )}
        </div>

        {/* Empty Cart State */}
        {cartItems.length === 0 ? (
          <motion.div className="cart-empty" variants={itemVariants}>
            <div className="empty-icon">🛍️</div>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added anything to your cart yet</p>
            <motion.button
              className="continue-shopping-btn"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => (window.location.href = "/")}
            >
              Start Shopping
            </motion.button>
          </motion.div>
        ) : (
          <div className="cart-content-grid">
            {/* Cart Items Section */}
            <div className="cart-items-section">
              <AnimatePresence mode="popLayout">
                {cartItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    className="cart-item"
                    variants={itemVariants}
                    layout
                    exit="exit"
                  >
                    <div className="item-image-wrapper">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="item-image"
                      />
                    </div>

                    <div className="item-info">
                      <h3 className="item-name">{item.name}</h3>
                      <p className="item-description">{item.description}</p>
                      <div className="item-price-mobile">
                        ${(item.price * item.quantity).toFixed(2)}
                      </div>
                    </div>

                    <div className="item-actions">
                      <div className="quantity-selector">
                        <motion.button
                          className={`qty-btn ${
                            item.quantity === 1 ? "delete-btn" : ""
                          }`}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => {
                            if (item.quantity === 1) {
                              removeFromCart(item.id);
                            } else {
                              updateQuantity(item.id, item.quantity - 1);
                            }
                          }}
                        >
                          {item.quantity === 1 ? (
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          ) : (
                            <span>−</span>
                          )}
                        </motion.button>
                        <span className="qty-display">{item.quantity}</span>
                        <motion.button
                          className="qty-btn"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() =>
                            updateQuantity(item.id, item.quantity + 1)
                          }
                        >
                          <span>+</span>
                        </motion.button>
                      </div>
                    </div>

                    <div className="item-price-desktop">
                      ${(item.price * item.quantity).toFixed(2)}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Order Summary Section */}
            <motion.div className="order-summary" variants={itemVariants}>
              <h2>Order Summary</h2>

              <div className="summary-details">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>${getCartTotal().toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span className="free-shipping">Free</span>
                </div>
                <div className="summary-row">
                  <span>Tax</span>
                  <span>${(getCartTotal() * 0.08).toFixed(2)}</span>
                </div>

                <div className="summary-divider"></div>

                <div className="summary-row total-row">
                  <span>Total</span>
                  <span className="total-amount">
                    ${(getCartTotal() * 1.08).toFixed(2)}
                  </span>
                </div>
              </div>

              <motion.button
                className="checkout-btn"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Proceed to Checkout
                <span className="arrow-icon">→</span>
              </motion.button>

              <div className="secure-checkout">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 1L3 3V7C3 10.5 5.5 13.5 8 14C10.5 13.5 13 10.5 13 7V3L8 1Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                  />
                  <path
                    d="M6 8L7.5 9.5L10 7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Secure checkout
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Cart;
